import { SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS } from "@common/utils/campaignBudgetMeta";
import { snapWallMinuteToMetaBudgetUtc } from "./metaBudgetScheduleWallSnap";
import {
  resolveEventWallTimezone,
  scheduleInputToEventWallMinute,
} from "./eventWallMinuteStrings";
import { wallMinuteToDayjsInEventZone } from "./wallMinutePickerDayjs";

const MIN_SCHEDULED_PHASE_MINUTES =
  SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS * 60;

/**
 * Default `time_start` / `time_end` for the **first** scheduled budget phase when switching to
 * **Scheduled**: prefer campaign **schedule** (guided wall `start_at` / `end_at`) when both are
 * set, otherwise **flight** ISO bounds (e.g. Ads Engage edit modal from ad set schedules).
 */
export function initialScheduledPhaseWallRange(params: {
  eventTimezone?: string;
  scheduleStartWall?: string;
  scheduleEndWall?: string;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
}): { time_start: string; time_end: string } {
  const tz = resolveEventWallTimezone(params.eventTimezone);
  const normalizePair = (
    a: string,
    b: string
  ): { time_start: string; time_end: string } | null => {
    const t0 = scheduleInputToEventWallMinute(a.trim(), tz);
    const t1 = scheduleInputToEventWallMinute(b.trim(), tz);
    return t0 && t1 ? { time_start: t0, time_end: t1 } : null;
  };
  const ss = params.scheduleStartWall?.trim() ?? "";
  const se = params.scheduleEndWall?.trim() ?? "";
  if (ss && se) {
    const out = normalizePair(ss, se);
    if (out) return out;
  }
  const fs = params.flightStartIso?.trim() ?? "";
  const fe = params.flightEndIso?.trim() ?? "";
  if (fs && fe) {
    const out = normalizePair(fs, fe);
    if (out) return out;
  }
  return { time_start: "", time_end: "" };
}

export type ScheduledPhaseWallRow = {
  time_start: string;
  time_end: string;
};

/**
 * Default **two** contiguous phase wall rows when switching to scheduled (or seeding empty rows).
 * Splits {@link initialScheduledPhaseWallRange} so each phase meets Meta’s minimum duration
 * when the full range is long enough; otherwise returns two empty rows for the user to fill.
 */
export function initialTwoScheduledPhaseWallRows(params: {
  eventTimezone?: string;
  scheduleStartWall?: string;
  scheduleEndWall?: string;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
}): [ScheduledPhaseWallRow, ScheduledPhaseWallRow] {
  const emptyPair: [ScheduledPhaseWallRow, ScheduledPhaseWallRow] = [
    { time_start: "", time_end: "" },
    { time_start: "", time_end: "" },
  ];
  const tz = resolveEventWallTimezone(params.eventTimezone);
  const full = initialScheduledPhaseWallRange(params);
  if (!full.time_start?.trim() || !full.time_end?.trim()) {
    return emptyPair;
  }

  const startWall = snapWallMinuteToMetaBudgetUtc(
    full.time_start,
    tz,
    "start"
  );
  const endWall = snapWallMinuteToMetaBudgetUtc(full.time_end, tz, "end");
  const d0 = wallMinuteToDayjsInEventZone(startWall, tz);
  const d1 = wallMinuteToDayjsInEventZone(endWall, tz);
  if (!d0?.isValid() || !d1?.isValid() || !d1.isAfter(d0)) {
    return emptyPair;
  }

  const totalMin = d1.diff(d0, "minute");
  if (totalMin < MIN_SCHEDULED_PHASE_MINUTES * 2) {
    return emptyPair;
  }

  const phase1Minutes = Math.max(
    MIN_SCHEDULED_PHASE_MINUTES,
    Math.min(Math.floor(totalMin / 2), totalMin - MIN_SCHEDULED_PHASE_MINUTES)
  );
  if (totalMin - phase1Minutes < MIN_SCHEDULED_PHASE_MINUTES) {
    return emptyPair;
  }

  const mid = d0.add(phase1Minutes, "minute");
  const junction = snapWallMinuteToMetaBudgetUtc(
    mid.format("YYYY-MM-DDTHH:mm"),
    tz,
    "start"
  );
  const dMid = wallMinuteToDayjsInEventZone(junction, tz);
  if (
    !dMid?.isValid() ||
    !dMid.isAfter(d0) ||
    !d1.isAfter(dMid) ||
    dMid.diff(d0, "minute") < MIN_SCHEDULED_PHASE_MINUTES ||
    d1.diff(dMid, "minute") < MIN_SCHEDULED_PHASE_MINUTES
  ) {
    return emptyPair;
  }

  return [
    { time_start: startWall, time_end: junction },
    { time_start: junction, time_end: endWall },
  ];
}
